import { useState, useEffect } from 'react';
import API, { formatError } from '../api/client';
import { Plus, Edit, Trash2, Calendar, X, List, ChevronLeft, ChevronRight } from 'lucide-react';
import PatientSelect from '../components/PatientSelect';

const STATUTS = ['planifié', 'confirmé', 'terminé', 'annulé'];
const STATUT_COLORS = {
  'planifié': 'bg-blue-100 text-blue-700',
  'confirmé': 'bg-emerald-100 text-emerald-700',
  'terminé': 'bg-stone-100 text-stone-600',
  'annulé': 'bg-red-100 text-red-700',
};
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const EMPTY_FORM = {
  patient_id: '', date: new Date().toISOString().split('T')[0],
  heure: '09:00', motif: '', statut: 'planifié', notes: ''
};

function CalendarView({ appointments, onDayClick, selectedDate }) {
  const [viewDate, setViewDate] = useState(new Date());
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Monday-first: 0=Mon,...,6=Sun
  let startDow = firstDay.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const apptByDate = {};
  appointments.forEach(a => {
    if (!apptByDate[a.date]) apptByDate[a.date] = [];
    apptByDate[a.date].push(a);
  });

  const today = new Date().toISOString().split('T')[0];
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, dateStr, appts: apptByDate[dateStr] || [] });
  }

  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      {/* Calendar header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
        <button onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-2 hover:bg-stone-100 rounded-lg transition-colors">
          <ChevronLeft size={18} className="text-stone-600" />
        </button>
        <h3 className="font-heading font-semibold text-stone-800">{MONTHS_FR[month]} {year}</h3>
        <button onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-2 hover:bg-stone-100 rounded-lg transition-colors">
          <ChevronRight size={18} className="text-stone-600" />
        </button>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-stone-100">
        {DAYS_FR.map(d => (
          <div key={d} className="py-2 text-center text-xs font-semibold text-stone-500 uppercase">
            {d}
          </div>
        ))}
      </div>
      {/* Days grid */}
      <div className="grid grid-cols-7">
        {cells.map((cell, i) => {
          if (!cell) return <div key={`empty-${i}`} className="h-20 border-b border-r border-stone-50 last:border-r-0" />;
          const isToday = cell.dateStr === today;
          const isSelected = cell.dateStr === selectedDate;
          const hasAppts = cell.appts.length > 0;
          return (
            <div
              key={cell.dateStr}
              onClick={() => onDayClick(cell.dateStr)}
              className={`h-20 border-b border-r border-stone-50 last:border-r-0 p-1.5 cursor-pointer transition-colors
                ${isSelected ? 'bg-emerald-50' : 'hover:bg-stone-50'}
                ${(i + 1) % 7 === 0 ? 'border-r-0' : ''}`}
            >
              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium mb-1
                ${isToday ? 'bg-emerald-600 text-white' : isSelected ? 'bg-emerald-100 text-emerald-700' : 'text-stone-700'}`}
              >
                {cell.day}
              </div>
              {cell.appts.slice(0, 2).map(a => (
                <div key={a.id} className={`text-xs truncate px-1 rounded mb-0.5 ${STATUT_COLORS[a.statut] || 'bg-stone-100 text-stone-600'}`}>
                  {a.heure} {a.patient_nom?.split(' ')[1] || ''}
                </div>
              ))}
              {cell.appts.length > 2 && (
                <div className="text-xs text-stone-400 px-1">+{cell.appts.length - 2}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [dateFilter, setDateFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editAppt, setEditAppt] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'list'
  const [selectedCalDate, setSelectedCalDate] = useState(new Date().toISOString().split('T')[0]);

  const load = async (date = '') => {
    const params = date ? { date } : {};
    const [r, p] = await Promise.all([
      API.get('/appointments', { params }),
      API.get('/patients'),
    ]);
    setAppointments(r.data);
    setPatients(p.data.data ?? p.data);
  };

  useEffect(() => { load(dateFilter); }, [dateFilter]);

  const openAdd = (date = '') => {
    setForm({ ...EMPTY_FORM, date: date || new Date().toISOString().split('T')[0] });
    setEditAppt(null);
    setShowModal(true);
    setError('');
  };
  const openEdit = (a) => { setForm({ ...a, notes: a.notes || '' }); setEditAppt(a); setShowModal(true); setError(''); };

  const handleSave = async () => {
    setError('');
    if (!form.patient_id || !form.date || !form.heure || !form.motif) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setSaving(true);
    try {
      if (editAppt) {
        await API.put(`/appointments/${editAppt.id}`, form);
      } else {
        await API.post('/appointments', form);
      }
      setShowModal(false);
      load(dateFilter);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    await API.delete(`/appointments/${id}`);
    load(dateFilter);
  };

  const updateStatut = async (id, statut) => {
    const appt = appointments.find(a => a.id === id);
    if (!appt) return;
    await API.put(`/appointments/${id}`, { ...appt, statut });
    load(dateFilter);
  };

  const handleCalendarDayClick = (dateStr) => {
    setSelectedCalDate(dateStr);
    setDateFilter(dateStr);
  };

  const F = ({ label, children }) => (
    <div className="flex flex-col space-y-1">
      <label className="text-xs font-medium text-stone-600">{label}</label>
      {children}
    </div>
  );

  const inp = (field, type = 'text', placeholder = '') => (
    <input type={type} placeholder={placeholder} value={form[field] || ''}
      onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
      className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
  );

  const selectedDayAppts = appointments.filter(a => a.date === (dateFilter || selectedCalDate));

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="appointments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900">Rendez-vous</h1>
          <p className="text-stone-500 text-sm mt-0.5">{appointments.length} rendez-vous</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* View toggle */}
          <div className="flex bg-stone-100 rounded-lg p-1">
            <button
              data-testid="calendar-view-btn"
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'calendar' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-500 hover:text-stone-700'}`}
            >
              <Calendar size={15} />
              Calendrier
            </button>
            <button
              data-testid="list-view-btn"
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-500 hover:text-stone-700'}`}
            >
              <List size={15} />
              Liste
            </button>
          </div>

          {viewMode === 'list' && (
            <>
              <input
                data-testid="date-filter-input"
                type="date"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
              {dateFilter && (
                <button onClick={() => setDateFilter('')} className="text-stone-400 hover:text-stone-600 p-1">
                  <X size={16} />
                </button>
              )}
            </>
          )}
          <button
            data-testid="add-appointment-button"
            onClick={() => openAdd(viewMode === 'calendar' ? selectedCalDate : '')}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus size={17} />
            Nouveau RDV
          </button>
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <CalendarView
              appointments={appointments}
              onDayClick={handleCalendarDayClick}
              selectedDate={selectedCalDate}
            />
          </div>
          {/* Day detail panel */}
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <h3 className="font-heading font-semibold text-stone-800 text-sm">
                {selectedCalDate ? new Date(selectedCalDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Sélectionnez un jour'}
              </h3>
              <button
                onClick={() => openAdd(selectedCalDate)}
                className="p-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                title="Ajouter RDV"
              >
                <Plus size={14} />
              </button>
            </div>
            <div className="divide-y divide-stone-50 max-h-96 overflow-y-auto">
              {selectedDayAppts.length === 0 && (
                <p className="text-center text-stone-400 py-8 text-sm">
                  <Calendar size={28} className="text-stone-200 mx-auto mb-2" />
                  Aucun RDV ce jour
                </p>
              )}
              {selectedDayAppts.sort((a, b) => a.heure.localeCompare(b.heure)).map(a => (
                <div key={a.id} className="px-4 py-3 hover:bg-stone-50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-600 font-bold text-sm">{a.heure}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${STATUT_COLORS[a.statut] || 'bg-stone-100 text-stone-600'}`}>
                          {a.statut}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-stone-800 mt-0.5">{a.patient_nom}</p>
                      <p className="text-xs text-stone-500 truncate">{a.motif}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(a)} className="p-1.5 text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                        <Edit size={13} />
                      </button>
                      <button onClick={() => handleDelete(a.id)} className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* List view */
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <table className="w-full" data-testid="appointments-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {['Date', 'Heure', 'Patient', 'Motif', 'Statut', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {appointments.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-stone-400 text-sm">
                  <Calendar size={36} className="text-stone-200 mx-auto mb-2" />
                  Aucun rendez-vous
                </td></tr>
              )}
              {appointments.map(a => (
                <tr key={a.id} className="hover:bg-stone-50/60 transition-colors" data-testid="appointment-table-row">
                  <td className="px-4 py-3 text-sm font-medium text-stone-800">{a.date}</td>
                  <td className="px-4 py-3 text-sm text-emerald-600 font-semibold">{a.heure}</td>
                  <td className="px-4 py-3 text-sm text-stone-700">{a.patient_nom || '—'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600 max-w-[200px] truncate">{a.motif}</td>
                  <td className="px-4 py-3">
                    <select
                      value={a.statut}
                      onChange={e => updateStatut(a.id, e.target.value)}
                      className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer focus:outline-none ${STATUT_COLORS[a.statut] || 'bg-stone-100 text-stone-600'}`}
                    >
                      {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => openEdit(a)} className="p-1.5 text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleDelete(a.id)} className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" data-testid="appointment-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
              <h2 className="font-heading font-semibold text-stone-800">
                {editAppt ? 'Modifier le RDV' : 'Nouveau Rendez-vous'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1 text-stone-400 hover:text-stone-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">{error}</div>}
              <F label="Patient *">
                <PatientSelect
                  value={form.patient_id}
                  patients={patients}
                  onChange={(id) => setForm(f => ({ ...f, patient_id: id }))}
                />
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Date *">{inp('date', 'date')}</F>
                <F label="Heure *">{inp('heure', 'time')}</F>
              </div>
              <F label="Motif *">
                <input type="text" placeholder="Motif du rendez-vous" value={form.motif}
                  onChange={e => setForm(f => ({ ...f, motif: e.target.value }))}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
              </F>
              <F label="Statut">
                <select value={form.statut} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                  {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </F>
              <F label="Notes">
                <textarea rows={2} placeholder="Notes..." value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
              </F>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone-200 bg-stone-50">
              <button onClick={() => setShowModal(false)} className="px-5 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-100">Annuler</button>
              <button
                data-testid="save-appointment-button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
